import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import html2canvas from 'html2canvas';
import '../styles/FeedbackModal.css';

const FeedbackModal = ({ onClose, currentPage }) => {
    const [subject, setSubject] = useState('');
    const [content, setContent] = useState('');
    const [screenshot, setScreenshot] = useState(null);
    const [screenshotPreviewUrl, setScreenshotPreviewUrl] = useState(null);
    const [isCapturing, setIsCapturing] = useState(false);
    const [isSending, setIsSending] = useState(false);

    useEffect(() => {
        // Capture screenshot when modal opens
        captureScreenshot();
    }, []);

    useEffect(() => {
        if (!screenshot) {
            setScreenshotPreviewUrl(null);
            return;
        }

        const nextPreviewUrl = URL.createObjectURL(screenshot);
        setScreenshotPreviewUrl(nextPreviewUrl);

        return () => {
            URL.revokeObjectURL(nextPreviewUrl);
        };
    }, [screenshot]);

    const captureScreenshot = async () => {
        setIsCapturing(true);
        try {
            // Hide the modal temporarily for screenshot
            const modal = document.querySelector('.modal-overlay');
            if (modal) {
                modal.style.display = 'none';
            }

            // Wait a moment for the modal to be hidden
            await new Promise(resolve => setTimeout(resolve, 100));

            // Capture the screenshot
            const canvas = await html2canvas(document.body, {
                height: window.innerHeight,
                width: window.innerWidth,
                scrollX: 0,
                scrollY: 0,
                useCORS: true,
                allowTaint: true,
                scale: 0.5 // Reduce quality for smaller file size
            });

            // Convert to blob
            canvas.toBlob((blob) => {
                setScreenshot(blob);
            }, 'image/jpeg', 0.8);

            // Show the modal again
            if (modal) {
                modal.style.display = 'flex';
            }
        } catch (error) {
            console.error('Error capturing screenshot:', error);
        } finally {
            setIsCapturing(false);
        }
    };

    const handleSend = async () => {
        if (!subject.trim() || !content.trim()) {
            alert('Please fill in both subject and content fields.');
            return;
        }

        setIsSending(true);

        try {
            const formData = new FormData();
            formData.append('subject', subject);
            formData.append('content', content);
            formData.append('currentPage', currentPage);
            formData.append('userAgent', navigator.userAgent);
            formData.append('timestamp', new Date().toISOString());
            formData.append('url', window.location.href);
            
            if (screenshot) {
                formData.append('screenshot', screenshot, 'screenshot.jpg');
            }

            const response = await fetch('https://api.casedb.co/feedback.php', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            const result = await response.json();

            if (result.success) {
                alert('Feedback sent successfully! Thank you for your input.');
                onClose();
            } else {
                alert('Failed to send feedback. Please try again.');
            }
        } catch (error) {
            console.error('Error sending feedback:', error);
            alert('Failed to send feedback. Please try again.');
        } finally {
            setIsSending(false);
        }
    };

    const modalFooter = (
        <div className="modal-footer-actions">
            <button 
                className="action alt" 
                onClick={onClose}
                disabled={isSending}
            >
                Cancel
            </button>
            <button 
                className="action" 
                onClick={handleSend}
                disabled={isSending || !subject.trim() || !content.trim()}
            >
                {isSending ? 'Sending...' : 'Send Feedback'}
            </button>
        </div>
    );

    return (
        <Modal 
            title="Send Feedback" 
            onClose={onClose}
            footer={modalFooter}
            wide={true}
        >
            <div className="modal-content-wrapper feedback-modal">
                <div className="form-group full nm">
                    <label htmlFor="feedback-subject" className='subtext'>Subject</label>
                    <input
                        id="feedback-subject"
                        type="text"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Brief description of your feedback"
                        disabled={isSending}
                    />
                </div>

                <div className="form-group full nm">
                    <label htmlFor="feedback-content" className='subtext'>Details</label>
                    <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="Please provide detailed feedback, including steps to reproduce any issues..."
                        rows="6"
                        disabled={isSending}
                    />
                </div>

                <div className="form-group nm">
                    <label className='subtext'>Page Information</label>
                    <input type='text' value={currentPage} readOnly disabled />
                </div>

                <div className="form-group screenshot-modal-container">
                    <label className='subtext'>Screenshot</label>
                    <div className="screenshot-container">
                        {isCapturing ? (
                            <div className="screenshot-loading">
                                Capturing screenshot...
                            </div>
                        ) : screenshot ? (
                            <div className="screenshot-preview">
                                <img 
                                    src={screenshotPreviewUrl || ''} 
                                    alt="Page screenshot" 
                                />
                                <div className="screenshot-info">
                                    Screenshot captured automatically
                                </div>
                            </div>
                        ) : (
                            <div className="screenshot-error">
                                Failed to capture screenshot
                                <button 
                                    onClick={captureScreenshot}
                                    className="btn-small"
                                >
                                    Retry
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default FeedbackModal;